import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { StatsigClient } from '@statsig/js-client';
import type { StatsigUser } from '@statsig/js-client';
import config from 'uplisting-frontend/config/environment';
import { storage, StorageKey } from 'uplisting-frontend/utils/storage';

// E2E-only: reads the gate overrides the harness seeds into localStorage before
// app boot, so it is read once and memoised. Returns undefined for a gate that
// is unset or not a boolean, letting the dev default of `true` stand.
let e2eGates: Record<string, unknown> | undefined;

// Boot blocks on this fetch, so it is far tighter than the SDK default of 10s.
const NETWORK_TIMEOUT_MS = 3_000;

const ANONYMOUS_USER_ID = 'anonymous';

function e2eGateOverride(gateName: string): boolean | undefined {
  e2eGates ??=
    storage.local.get<Record<string, unknown>>(StorageKey.E2E_FEATURE_GATES) ??
    {};

  const value = e2eGates[gateName];

  return typeof value === 'boolean' ? value : undefined;
}

export default class FeatureFlagsService extends Service {
  @service session!: Services['session'];
  @service sentry!: Services['sentry'];

  @tracked _revision = 0;

  #client?: StatsigClient;
  #userId?: string;

  setClient(client: StatsigClient): void {
    this.#client = client;
    this.#userId = undefined;
    this._revision++;
  }

  async initialize(): Promise<void> {
    if (!config.statsig.clientSdkKey) {
      return;
    }

    const statsigUser = this.#buildUser();

    if (this.#client) {
      await this.#switchUser(statsigUser);

      return;
    }

    try {
      this.#client = new StatsigClient(
        config.statsig.clientSdkKey,
        statsigUser,
        {
          environment: { tier: config.statsig.environment },
          networkConfig: { networkTimeoutMs: NETWORK_TIMEOUT_MS },
        },
      );

      await this.#client.initializeAsync();

      this.#userId = statsigUser.userID;
      this._revision++;
    } catch (error) {
      this.#client = undefined;

      this.sentry.captureException(error);
    }
  }

  async refresh(): Promise<void> {
    if (!this.#client) {
      return;
    }

    try {
      await this.#client.updateUserAsync(this.#buildUser());

      this._revision++;
    } catch (error) {
      this.sentry.captureException(error);
    }
  }

  // For gates that decide access to the whole app: no development
  // defaults-to-true, and no cached evaluation, since a stale `true` would lock
  // users out of a healthy app with no way back in.
  liveGateEnabled(gateName: string): boolean {
    void this._revision;

    if (config.IS_DEV) {
      return e2eGateOverride(gateName) ?? false;
    }

    const gate = this.#client?.getFeatureGate(gateName);

    if (!gate || gate.details.reason.startsWith('Cache')) {
      return false;
    }

    return gate.value;
  }

  #buildUser(): StatsigUser {
    const { currentUser } = this.session;

    // Signed out there is no user yet, but the maintenance gate is read at boot
    // and every Statsig ID type on this project is userID-based, so send a
    // shared constant rather than nothing at all.
    if (!currentUser) {
      return { userID: ANONYMOUS_USER_ID };
    }

    const userId = currentUser.id;
    const accountId = currentUser.accountId ?? userId;

    return {
      userID: `account:${accountId}`,
      customIDs: {
        user_id: userId,
        account_id: accountId,
      },
      custom: {
        user_id: userId,
        account_id: accountId,
      },
    };
  }

  async #switchUser(statsigUser: StatsigUser): Promise<void> {
    if (this.#userId === statsigUser.userID) {
      return;
    }

    try {
      await this.#client?.updateUserAsync(statsigUser);

      this.#userId = statsigUser.userID;
      this._revision++;
    } catch (error) {
      this.sentry.captureException(error);
    }
  }

  get gate() {
    // Consume tracked revision so Glimmer re-renders when client initializes.
    void this._revision;

    return {
      enabled: (gateName: string): boolean => {
        // In development all gates default to true
        // to match the BE behaviour (Gate.enabled? returns true in development).
        // The E2E harness can force a specific gate off/on via a localStorage
        // seed so flag-off paths are testable (mirrors the BE /e2e override).
        if (config.IS_DEV) {
          return e2eGateOverride(gateName) ?? true;
        }

        return this.#client?.checkGate(gateName) ?? false;
      },
    };
  }

  get config() {
    void this._revision;

    return {
      param: <T>(configName: string, paramName: string, defaultValue: T): T => {
        return (
          (this.#client
            ?.getDynamicConfig(configName)
            .get(paramName, defaultValue) as T) ?? defaultValue
        );
      },

      params: <T extends Record<string, unknown>>(
        configName: string,
        defaults: T,
      ): T => {
        return this.#getParams(
          defaults,
          (name) => this.#client?.getDynamicConfig(name),
          configName,
        );
      },
    };
  }

  // Uses Statsig layers for lazy exposure logging.
  get experiment() {
    void this._revision;

    return {
      param: <T>(
        experimentName: string,
        paramName: string,
        defaultValue: T,
      ): T => {
        return (
          (this.#client
            ?.getLayer(experimentName)
            .get(paramName, defaultValue) as T) ?? defaultValue
        );
      },

      params: <T extends Record<string, unknown>>(
        experimentName: string,
        defaults: T,
      ): T => {
        return this.#getParams(
          defaults,
          (name) => this.#client?.getLayer(name),
          experimentName,
        );
      },
    };
  }

  async shutdown(): Promise<void> {
    if (this.#client) {
      await this.#client.shutdown();
      this.#client = undefined;
      this.#userId = undefined;
    }
  }

  #getParams<T extends Record<string, unknown>>(
    defaults: T,
    fetcher: (
      name: string,
    ) => { get: (key: string, fallback: unknown) => unknown } | undefined,
    name: string,
  ): T {
    const obj = fetcher(name);

    if (!obj) {
      return defaults;
    }

    const result = { ...defaults };

    for (const key of Object.keys(defaults)) {
      result[key as keyof T] = obj.get(key, defaults[key]) as T[keyof T];
    }

    return result;
  }
}

declare module '@ember/service' {
  interface Registry {
    'feature-flags': FeatureFlagsService;
  }
}
